/*
 * Copyright (c) 2023 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { ComponentStore } from "@ngrx/component-store";
import { Injectable } from "@angular/core";
import { Provider } from "@angular/core";
import { AnvilorFormsAppState } from "./anvilor-forms-app.state";
import { RuiInstance, RuiStore } from "@anvilor/ngx-rui";
import { ThemeService } from "@anvilor/ngx-theming";
import { QueryParamService } from "@anvilor/ngx-apps-core";
import { API_CALL_NONE } from "@anvilor/ngx-apps-core";
import { AnvilorNotificationsService } from "@anvilor/ngx-apps-core";
import { WindowService } from "@anvilor/ngx-apps-core";
import { AnvilorFormsAppDebugTools } from "./debug-tools/debug-tools";
import { AnvilorFormsApiService } from "../services/anvilor-forms-api.service";
import { AnvilorFormsAppCoffeeUtil } from "./coffee/coffee";
import { Observable } from "rxjs";
import { switchMap } from "rxjs";
import { EelService } from "@anvilor/ngx-eel";
import { AppAuthenticationService } from "./auth/app-authentication";
import { FormAppLoading } from "./loading/form-app-loading";
import { AnvilorFormDisplayConfig } from "../models/anvilor-form-document";
import { filterNullish } from "@anvilor/ngx-core-utils";
import { AnvilorFormsAppOpenClose } from "./open-close/open-close";
import { AnvilorFormsAppEelOperators } from "./eel-operators/eel-operators";
import { AnvilorFormsAppTheming } from "./theming/app-theming";
import { AnvilorFormsAppWindowBinding } from "./window/window-binding";
import { AnvilorFormsAppSubmit } from "./submit/app-submit";
import { AnvilorFormSubmissionUXConfig } from "../models/anvilor-form-config";
import { filterUndefined } from "@anvilor/ngx-core-utils";
import { asSignal } from "@anvilor/ngx-core-utils";
import { RecaptchaService } from "../services/google-recaptcha.service";
import { AnvilorFormsAppCaptcha } from "./captcha/app-captcha";
import { AnvilorFormsAppAutosave } from "./autosave/app-autosave";
import { AnvilorFormsAppDensity } from "./theming/app-density";

const INITIAL_STATE: AnvilorFormsAppState = {
  is_debug_mode: false,
  force_enable_submit: false,
  anvilor_form_config_load_status: "initial",
  client_data_load_status: "initial",
  edit_config_load_status: "initial",
  auth_load_status: "initial",
  response_data_load_status: "initial",
  rui_initialization_status: "loading_form",
  post_response_call_status: API_CALL_NONE,
  submission_accepted: false,
};

/**
 * The store object for the general state management.
 *
 * We include a few key selectors here that are purely selecting state, but most
 * feature-specifc selectors are defined in supporting feature classes.
 */
@Injectable()
export class AnvilorFormsAppStore extends ComponentStore<AnvilorFormsAppState> {
  readonly isDebugMode$: Observable<boolean>;
  readonly envKey$: Observable<string>;
  readonly isNonLiveEnv$: Observable<boolean | undefined>;
  readonly clientData$: Observable<any>;
  readonly anvilorFormSubmissionUxConfig$: Observable<AnvilorFormSubmissionUXConfig>;
  readonly displayConfig$: Observable<AnvilorFormDisplayConfig>;
  readonly submissionClientData$: Observable<any>;
  readonly disabledAnonymousSubmissions$: Observable<boolean>;
  readonly isAnonymousSubmission$: Observable<boolean>;
  readonly autosaveEnabled$: Observable<boolean>;

  constructor() {
    super(INITIAL_STATE);
    this.anvilorFormSubmissionUxConfig$ = this.select(
      (state) => state.anvilor_form_config
    ).pipe(filterNullish());
    this.isDebugMode$ = this.select((state) => state.is_debug_mode);
    this.envKey$ = this.select(
      (state) => state.anvilor_form_config?.deployment_key ?? "live"
    );
    this.isNonLiveEnv$ = this.select(this.envKey$, (k) => k !== "live");
    this.submissionClientData$ = this.select((state) => state.response_client_data);
    this.displayConfig$ = this.select(
      this.anvilorFormSubmissionUxConfig$,
      (doc) => doc.display_config
    );
    this.clientData$ = this.select((state) => state.client_data).pipe(
      filterUndefined()
    );

    this.disabledAnonymousSubmissions$ = this.select(
      this.anvilorFormSubmissionUxConfig$,
      (config) => config.disable_anonymous_submissions ?? false
    );

    this.isAnonymousSubmission$ = this.select(
      (state) => state.edit_config_load_status === "complete"
    ).pipe(
      asSignal(),
      switchMap(() => this.select((state) => !state.edit_config))
    );

    this.autosaveEnabled$ = this.selectFromDisplayConfig(
      (c) => c.enable_autosave ?? false
    );
  }

  /**
   * A simple hack for having the store instance as a whole subscribe to an
   * observable.
   *
   * We create a void effect, switch it's trigger to be our observable, and then
   * call the effect.
   *
   * This gets us the benefits of letting ngrx take care of the unsubscribing,
   * etc.
   */
  subscribeTo(obs$: Observable<any>): void {
    this.effect<void>(($) => $.pipe(switchMap(() => obs$)))();
  }

  /**
   * Utility/convenience to select from the form document.
   */
  selectFromAnvilorFormConfig<T = any>(
    projector: (doc: AnvilorFormSubmissionUXConfig) => T
  ): Observable<T> {
    return this.select(this.anvilorFormSubmissionUxConfig$, projector);
  }

  /**
   * Utility/convenience to select from the document's display config.
   */
  selectFromDisplayConfig<T = any>(
    projector: (c: AnvilorFormDisplayConfig) => T
  ): Observable<T> {
    return this.select(this.displayConfig$, projector);
  }
}

/**
 * Simple object to bundle all the services we want to pass around.
 */
@Injectable()
export class AnvilorFormsAppStoreContext {
  constructor(
    public store: AnvilorFormsAppStore,
    public ruiLegacy: RuiStore,
    public ruiInstance: RuiInstance,
    public theme: ThemeService,
    public qparams: QueryParamService,
    public formsApi: AnvilorFormsApiService,
    public notify: AnvilorNotificationsService,
    public eel: EelService,
    public auth: AppAuthenticationService,
    public window: WindowService,
    public recaptcha: RecaptchaService
  ) {}
}

/**
 * The main object/interface to provide to the app / component.
 */
@Injectable()
export class AnvilorFormsApp {
  // Self-contained utilities only needed internally at this top level
  private debug: AnvilorFormsAppDebugTools;
  private coffee: AnvilorFormsAppCoffeeUtil;
  private window: AnvilorFormsAppWindowBinding;
  private eelOps: AnvilorFormsAppEelOperators;
  private readonly autosave: AnvilorFormsAppAutosave;

  // Utility/API classes that are exposed or work with others.
  readonly loading: FormAppLoading;
  readonly openClose: AnvilorFormsAppOpenClose;
  readonly theming: AnvilorFormsAppTheming;
  readonly density: AnvilorFormsAppDensity;
  readonly captcha: AnvilorFormsAppCaptcha;
  readonly submit: AnvilorFormsAppSubmit;

  /**
   * Helper to get the types for providing.
   */
  static providers(): Provider[] {
    return [
      AnvilorFormsApp,
      AnvilorFormsAppStoreContext,
      AnvilorFormsAppStore,
      // ngx-rui 1.0 requires RuiInstance to be registered via provide(), which
      // also supplies the RUI_CONTROL_COMPONENT_PROVIDERS token it injects.
      ...RuiInstance.provide(),
      ...AppAuthenticationService.providers(),
    ];
  }

  constructor(
    private ctx: AnvilorFormsAppStoreContext,
    public readonly store: AnvilorFormsAppStore
  ) {
    this.debug = new AnvilorFormsAppDebugTools(ctx);
    this.coffee = new AnvilorFormsAppCoffeeUtil(ctx);
    this.eelOps = new AnvilorFormsAppEelOperators(this, ctx);
    this.window = new AnvilorFormsAppWindowBinding(ctx);
    this.autosave = new AnvilorFormsAppAutosave(ctx);
    this.loading = new FormAppLoading(this.autosave, ctx);
    this.openClose = new AnvilorFormsAppOpenClose(ctx);
    this.theming = new AnvilorFormsAppTheming(ctx);
    this.density = new AnvilorFormsAppDensity(ctx);
    this.captcha = new AnvilorFormsAppCaptcha(ctx);
    this.submit = new AnvilorFormsAppSubmit(this, ctx);
  }

  initialize(): void {
    this.debug.initialize();
    this.autosave.initialize();
    this.coffee.startCoffee();
    this.loading.load();
    this.theming.bindTheme();
    this.density.bindDensity();
    this.window.bindToWindow();
    this.eelOps.registerAll();
  }

  subscribeTo(obs$: Observable<any>): void {
    return this.store.subscribeTo(obs$);
  }
}
